import * as signalR from '@microsoft/signalr';
import type { RealTimeMetricsDto } from '../types/dashboard';

export interface MetricsSignalREvents {
  onRealTimeMetricsUpdated?: (data: RealTimeMetricsDto) => void;
}

/**
 * SignalR service for the MetricsHub.
 */
class MetricsSignalRService {
  private connection: signalR.HubConnection | null = null;
  private isConnecting = false;
  private events: MetricsSignalREvents = {};

  /**
   * Connects to the metrics hub and subscribes to updates.
   *
   * NOTE: The backend tenant context middleware supports tenant/project via query params, so we include them
   * in the hub URL to ensure the hub can resolve the context for group subscriptions.
   */
  async connect(baseUrl: string, token: string, tenantId: string, projectId?: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      const url = new URL(`${baseUrl}/hubs/metrics`);
      url.searchParams.set('tenantId', tenantId);
      if (projectId) {
        url.searchParams.set('projectId', projectId);
      }

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(url.toString(), {
          accessTokenFactory: () => token,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect()
        .build();

      this.setupEventHandlers();

      await this.connection.start();
      await this.connection.invoke('SubscribeToMetrics');

      this.isConnecting = false;
    } catch (error) {
      this.isConnecting = false;
      throw error;
    }
  }

  /**
   * Disconnects from the metrics hub.
   */
  async disconnect(): Promise<void> {
    if (!this.connection) return;
    try {
      if (this.connection.state === signalR.HubConnectionState.Connected) {
        await this.connection.invoke('UnsubscribeFromMetrics');
      }
    } catch {
      // Ignore unsubscribe failures during teardown.
    }

    await this.connection.stop();
    this.connection = null;
    this.events = {};
  }

  /**
   * Registers event handlers.
   */
  on(events: MetricsSignalREvents): void {
    this.events = { ...this.events, ...events };
  }

  /**
   * Unregisters all event handlers.
   */
  off(): void {
    this.events = {};
  }

  getState(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    this.connection.on('RealTimeMetricsUpdated', (data: RealTimeMetricsDto) => {
      this.events.onRealTimeMetricsUpdated?.(data);
    });
  }
}

export const metricsSignalRService = new MetricsSignalRService();

